import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminConciergeStats } from "../lib/api";

// Brutalist relative-time: "3m", "2h", "5d" — keeps the table tight.
function ago(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-black px-3 py-2 min-w-[120px]" data-testid={testid}>
      <div className="text-[20px] font-bold leading-none">{value}</div>
      <div className="text-[11px] text-[#666] mt-1">{label}</div>
    </div>
  );
}

/**
 * Admin "+ concierge" panel — aggregate analytics for the AI concierge.
 * Shows total questions, distinct conversations ("activations"), today /
 * 7-day counts, top searches, per-surface split, a 14-day trend, and the
 * most recent questions. Reads `concierge_queries` (no raw IP stored).
 */
export default function AdminConcierge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminConciergeStats());
    } catch {
      toast.error("failed to load concierge stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <section data-testid="admin-concierge" className="mt-4 border border-black p-4">
        <div className="text-[13px] text-[#888]">loading concierge stats...</div>
      </section>
    );
  }

  const d = data || {};
  const top = d.top_queries || [];
  const recent = d.recent || [];
  const surfaces = d.by_surface || [];
  const trend = d.daily_trend || [];
  const maxDay = Math.max(1, ...trend.map((t) => t.count || 0));
  const empty = (d.total_questions || 0) === 0;

  return (
    <section data-testid="admin-concierge" className="mt-4 border border-black p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-bold">{">"} concierge analytics</div>
        <button className="tv-link text-[12px]" onClick={load} data-testid="concierge-refresh">
          [refresh]
        </button>
      </div>
      <p className="text-[12px] text-[#666] mb-4">
        what people ask the AI concierge and how often it&apos;s used.{" "}
        <span className="tv-hl">no IP addresses</span> are stored here — rows
        auto-delete after {d.retention_days || 180} days.
      </p>

      {empty ? (
        <div className="text-[12px] text-[#888] border border-[#ddd] p-6 text-center" data-testid="concierge-empty">
          no concierge questions logged yet. ask the concierge a question (homepage,
          ⌘K, or the extension) and it&apos;ll show up here.
        </div>
      ) : (
        <>
          {/* headline stats */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="concierge-stats">
            <Stat label="questions (all time)" value={d.total_questions} testid="stat-total-questions" />
            <Stat label="conversations started" value={d.total_conversations} testid="stat-total-conversations" />
            <Stat label="questions today" value={d.questions_today} testid="stat-questions-today" />
            <Stat label="questions · 7d" value={d.questions_7d} testid="stat-questions-7d" />
            <Stat label="conversations · 7d" value={d.conversations_7d} testid="stat-conversations-7d" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* top searches */}
            <div data-testid="concierge-top">
              <div className="text-[12px] font-bold mb-2">top searches</div>
              {top.length === 0 ? (
                <div className="text-[11px] text-[#bbb]">none yet</div>
              ) : (
                <ol className="space-y-1">
                  {top.map((q, i) => (
                    <li
                      key={i}
                      className="flex items-baseline gap-2 text-[12px] border-b border-[#f0f0f0] py-1"
                      data-testid={`concierge-top-${i}`}
                    >
                      <span className="text-[#888] w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span className="flex-1 truncate" title={q.query}>{q.query}</span>
                      <span className="text-black font-bold shrink-0">{q.count}×</span>
                    </li>
                  ))}
                </ol>
              )}

              {/* by surface */}
              <div className="text-[12px] font-bold mt-5 mb-2">by surface</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]" data-testid="concierge-surfaces">
                {surfaces.map((s) => (
                  <span key={s.surface}>
                    <span className="tv-hl">{s.surface}</span>{" "}
                    <span className="text-[#666]">{s.count}</span>
                  </span>
                ))}
              </div>

              {/* 14-day trend */}
              <div className="text-[12px] font-bold mt-5 mb-2">last 14 days</div>
              <div className="flex items-end gap-1 h-[80px]" data-testid="concierge-trend">
                {trend.length === 0 ? (
                  <span className="text-[11px] text-[#bbb]">no recent activity</span>
                ) : (
                  trend.map((t) => (
                    <div key={t.date} className="flex-1 flex flex-col items-center justify-end h-full" title={`${t.date}: ${t.count}`}>
                      <div
                        className="w-full bg-black"
                        style={{ height: `${Math.round((t.count / maxDay) * 70)}px`, minHeight: t.count ? "2px" : "0" }}
                      />
                      <span className="text-[9px] text-[#aaa] mt-1">{t.date.slice(5)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* recent questions */}
            <div data-testid="concierge-recent">
              <div className="text-[12px] font-bold mb-2">recent questions</div>
              <div className="max-h-[420px] overflow-y-auto border border-[#eee]">
                {recent.length === 0 ? (
                  <div className="text-[11px] text-[#bbb] p-2">none yet</div>
                ) : (
                  recent.map((r, i) => (
                    <div
                      key={i}
                      className="px-2 py-1 text-[12px] border-b border-[#f0f0f0]"
                      data-testid={`concierge-recent-${i}`}
                    >
                      <div className="flex items-baseline gap-2">
                        <span className="text-[#aaa] text-[10px] shrink-0 w-8">{ago(r.ts)}</span>
                        <span className="flex-1">{r.query}</span>
                      </div>
                      <div className="text-[10px] text-[#bbb] pl-10">
                        {r.surface || "web"}{r.first_of_session ? " · new conversation" : ""}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
